/**
 * Universal Meter Service für Clara360
 * Unterstützt Techem, ISTA, Brunata, Minol und weitere Anbieter
 */

import { masterSupabase } from '../lib/supabase-master.js';

// Provider-spezifische Services
import TechemAPIService from './providers/TechemAPIService';
import IstaAPIService from './providers/IstaAPIService';
import BrunataAPIService from './providers/BrunataAPIService';

class UniversalMeterService {
  constructor(supabaseClient) {
    this.supabase = supabaseClient;
    this.providers = new Map();
    this.initializeProviders();
  }

  /**
   * Provider-Services initialisieren
   */
  initializeProviders() {
    // Techem
    if (process.env.REACT_APP_TECHEM_API_KEY) {
      this.providers.set('techem', new TechemAPIService(process.env.REACT_APP_TECHEM_API_KEY));
    }
    
    // ISTA
    if (process.env.REACT_APP_ISTA_API_KEY) {
      this.providers.set('ista', new IstaAPIService(process.env.REACT_APP_ISTA_API_KEY));
    }
    
    // Brunata
    if (process.env.REACT_APP_BRUNATA_API_KEY) {
      this.providers.set('brunata', new BrunataAPIService(process.env.REACT_APP_BRUNATA_API_KEY));
    }
    
    console.log(`[METER-SERVICE] ${this.providers.size} Provider initialisiert:`, Array.from(this.providers.keys()));
  }

  /**
   * Dashboard-Daten für eine Property abrufen (alle Provider)
   */
  async getDashboardData(propertyId) {
    try {
      console.log(`[METER-SERVICE] Lade Dashboard-Daten für Property ${propertyId}`);

      // Basis-Statistiken aus View abrufen
      const { data: stats, error: statsError } = await this.supabase
        .from('meter_dashboard_stats')
        .select('*')
        .eq('property_id', propertyId)
        .single();

      if (statsError && statsError.code !== 'PGRST116') {
        throw statsError;
      }

      // Geräte-Details aus View abrufen
      const { data: devices, error: devicesError } = await this.supabase
        .from('meter_device_overview')
        .select('*')
        .eq('property_id', propertyId)
        .order('installation_date', { ascending: false });

      if (devicesError) {
        throw devicesError;
      }

      // Provider-Konfigurationen abrufen
      const { data: providerConfigs, error: configError } = await this.supabase
        .from('meter_provider_configs')
        .select('*')
        .eq('property_id', propertyId);

      if (configError) {
        throw configError;
      }

      // Aktuelle Ablesungen abrufen
      const { data: recentReadings, error: readingsError } = await this.supabase
        .from('readings')
        .select(`
          *,
          meter_points!inner(
            external_id,
            provider_device_id,
            provider_name,
            meter_kind,
            location_description
          )
        `)
        .eq('meter_points.property_id', propertyId)
        .gte('reading_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('reading_at', { ascending: false })
        .limit(50);

      if (readingsError) {
        throw readingsError;
      }

      return {
        stats: stats || this.getDefaultStats(),
        devices: devices || [],
        recentReadings: recentReadings || [],
        providerConfigs: providerConfigs || [],
        lastUpdate: new Date().toISOString()
      };
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Dashboard-Daten-Abruf:', error);
      throw error;
    }
  }

  /**
   * Verfügbare Provider abrufen
   */
  getAvailableProviders() {
    return [
      {
        name: 'techem',
        displayName: 'Techem',
        description: 'Marktführer für Heizkostenverteiler und Wasserzähler',
        features: ['Fernauslesung', 'Automatische Abrechnung', 'Wartungsplanung'],
        apiAvailable: this.providers.has('techem'),
        website: 'https://www.techem.com'
      },
      {
        name: 'ista',
        displayName: 'ista',
        description: 'Internationaler Anbieter für Energieabrechnungen',
        features: ['EcoTrend Portal', 'Digitale Verbrauchsinformation', 'Smart Meter'],
        apiAvailable: this.providers.has('ista'),
        website: 'https://www.ista.com'
      },
      {
        name: 'brunata',
        displayName: 'Brunata',
        description: 'Traditioneller Anbieter für Heizkostenverteilung',
        features: ['Funkfernauslesung', 'Abrechnungsservice', 'Wartung'],
        apiAvailable: this.providers.has('brunata'),
        website: 'https://www.brunata.de'
      },
      {
        name: 'minol',
        displayName: 'Minol',
        description: 'Spezialist für Mess- und Abrechnungsdienstleistungen',
        features: ['ZENNER-Technologie', 'IoT-Lösungen', 'Digitale Services'],
        apiAvailable: this.providers.has('minol'),
        website: 'https://www.minol.de'
      },
      {
        name: 'kalorimeta',
        displayName: 'Kalorimeta',
        description: 'Regionaler Anbieter für Heizkostenabrechnungen',
        features: ['Regionale Betreuung', 'Individuelle Lösungen'],
        apiAvailable: this.providers.has('kalorimeta'),
        website: 'https://www.kalorimeta.de'
      },
      {
        name: 'manual',
        displayName: 'Manuelle Erfassung',
        description: 'Eigene Ablesung ohne externen Dienstleister',
        features: ['Vollständige Kontrolle', 'Keine Abhängigkeiten', 'Kostenlos'],
        apiAvailable: true,
        website: null
      }
    ];
  }

  /**
   * Provider für Property konfigurieren
   */
  async configureProvider(propertyId, providerName, config) {
    try {
      console.log(`[METER-SERVICE] Konfiguriere ${providerName} für Property ${propertyId}`);

      // Provider-Konfiguration speichern
      const { data, error } = await this.supabase
        .from('meter_provider_configs')
        .upsert({
          property_id: propertyId,
          provider_name: providerName,
          provider_property_id: config.propertyId,
          provider_customer_number: config.customerNumber,
          provider_contract_number: config.contractNumber,
          api_endpoint: config.apiEndpoint,
          api_version: config.apiVersion,
          authentication_method: config.authMethod,
          provider_config: config.providerSpecific || {},
          sync_enabled: true,
          config_status: 'active'
        })
        .select()
        .single();

      if (error) throw error;

      // Property als konfiguriert markieren
      await this.supabase
        .from('properties')
        .update({
          primary_meter_provider: providerName,
          provider_sync_status: 'configured'
        })
        .eq('id', propertyId);

      // Verbindung testen
      const testResult = await this.testProviderConnection(propertyId, providerName);
      
      if (testResult.success) {
        // Initiale Synchronisation auslösen
        await this.triggerSync(propertyId, providerName);
      }

      return { ...data, testResult };
    } catch (error) {
      console.error('[METER-SERVICE] Fehler bei Provider-Konfiguration:', error);
      throw error;
    }
  }

  /**
   * Provider-Verbindung testen
   */
  async testProviderConnection(propertyId, providerName) {
    try {
      console.log(`[METER-SERVICE] Teste ${providerName}-Verbindung für Property ${propertyId}`);

      const provider = this.providers.get(providerName);
      if (!provider) {
        return {
          success: false,
          error: `Provider ${providerName} nicht verfügbar oder nicht konfiguriert`
        };
      }

      // Provider-spezifischen Test durchführen
      const testResult = await provider.testConnection(propertyId);
      
      // Test-Ergebnis in Datenbank speichern
      await this.supabase.rpc('test_provider_connection', {
        p_property_id: propertyId,
        p_provider_name: providerName
      });

      return testResult;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Provider-Test:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Synchronisation für alle konfigurierten Provider auslösen
   */
  async triggerSync(propertyId, specificProvider = null) {
    try {
      console.log(`[METER-SERVICE] Löse Synchronisation aus für Property ${propertyId}`);

      // Provider-Konfigurationen abrufen
      let query = this.supabase
        .from('meter_provider_configs')
        .select('*')
        .eq('property_id', propertyId)
        .eq('sync_enabled', true);

      if (specificProvider) {
        query = query.eq('provider_name', specificProvider);
      }

      const { data: configs, error } = await query;
      if (error) throw error;

      const syncResults = [];

      // Für jeden konfigurierten Provider synchronisieren
      for (const config of configs) {
        try {
          const provider = this.providers.get(config.provider_name);
          if (!provider) {
            console.warn(`[METER-SERVICE] Provider ${config.provider_name} nicht verfügbar`);
            continue;
          }

          // Provider-spezifische Synchronisation
          const result = await provider.syncData(propertyId, config);
          syncResults.push({
            provider: config.provider_name,
            success: true,
            result: result
          });

          console.log(`[METER-SERVICE] ${config.provider_name} Sync erfolgreich`);
        } catch (providerError) {
          console.error(`[METER-SERVICE] ${config.provider_name} Sync fehlgeschlagen:`, providerError);
          syncResults.push({
            provider: config.provider_name,
            success: false,
            error: providerError.message
          });
        }
      }

      // Gesamtstatus aktualisieren
      const allSuccessful = syncResults.every(r => r.success);
      const anySuccessful = syncResults.some(r => r.success);
      
      let overallStatus = 'error';
      if (allSuccessful) overallStatus = 'success';
      else if (anySuccessful) overallStatus = 'partial';

      await this.supabase
        .from('properties')
        .update({
          provider_sync_status: overallStatus,
          last_provider_sync: new Date().toISOString()
        })
        .eq('id', propertyId);

      return {
        success: anySuccessful,
        results: syncResults,
        overallStatus: overallStatus
      };
    } catch (error) {
      console.error('[METER-SERVICE] Fehler bei Synchronisation:', error);
      throw error;
    }
  }

  /**
   * Service-Auftrag erstellen (provider-agnostisch)
   */
  async createServiceOrder(meterPointId, orderType, description, priority = 'normal') {
    try {
      console.log(`[METER-SERVICE] Erstelle ${orderType}-Auftrag für Meter ${meterPointId}`);

      // Meter-Point-Details abrufen
      const { data: meterPoint, error: meterError } = await this.supabase
        .from('meter_device_overview')
        .select('*')
        .eq('id', meterPointId)
        .single();

      if (meterError) throw meterError;

      // Service-Auftrag in Datenbank erstellen
      const { data: serviceOrder, error: orderError } = await this.supabase
        .from('meter_service_orders')
        .insert({
          meter_point_id: meterPointId,
          property_id: meterPoint.property_id,
          provider_name: meterPoint.provider_name || 'manual',
          order_type: orderType,
          issue_description: description,
          priority: priority,
          order_status: 'created',
          requested_date: new Date().toISOString().split('T')[0]
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Provider-spezifische Übermittlung
      if (meterPoint.provider_name && meterPoint.provider_name !== 'manual') {
        const provider = this.providers.get(meterPoint.provider_name);
        if (provider) {
          try {
            const providerResult = await provider.submitServiceOrder(serviceOrder);
            
            // Provider-Response speichern
            await this.supabase
              .from('meter_service_orders')
              .update({
                provider_order_id: providerResult.orderId,
                order_status: 'submitted',
                provider_response: providerResult
              })
              .eq('id', serviceOrder.id);
              
            console.log(`[METER-SERVICE] Auftrag an ${meterPoint.provider_name} übermittelt`);
          } catch (providerError) {
            console.error(`[METER-SERVICE] Provider-Übermittlung fehlgeschlagen:`, providerError);
            
            // Fehler-Status setzen
            await this.supabase
              .from('meter_service_orders')
              .update({
                order_status: 'submission_failed',
                provider_response: { error: providerError.message }
              })
              .eq('id', serviceOrder.id);
          }
        }
      }

      return serviceOrder;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Service-Auftrag:', error);
      throw error;
    }
  }

  /**
   * Alle konfigurierten Provider für Property abrufen
   */
  async getPropertyProviders(propertyId) {
    try {
      const { data, error } = await this.supabase
        .from('meter_provider_configs')
        .select('*')
        .eq('property_id', propertyId)
        .order('provider_name');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Provider-Abruf:', error);
      throw error;
    }
  }

  /**
   * Provider-Statistiken abrufen
   */
  async getProviderStatistics(propertyId) {
    try {
      const { data: devices, error } = await this.supabase
        .from('meter_device_overview')
        .select('provider_name, meter_kind, device_status')
        .eq('property_id', propertyId);

      if (error) throw error;

      // Statistiken berechnen
      const stats = {
        byProvider: {},
        byType: {},
        byStatus: {},
        total: devices.length
      };

      devices.forEach(device => {
        const provider = device.provider_name || 'manual';
        
        // Nach Provider
        if (!stats.byProvider[provider]) {
          stats.byProvider[provider] = { total: 0, active: 0, warning: 0, error: 0 };
        }
        stats.byProvider[provider].total++;
        stats.byProvider[provider][device.device_status]++;
        
        // Nach Typ
        stats.byType[device.meter_kind] = (stats.byType[device.meter_kind] || 0) + 1;
        
        // Nach Status
        stats.byStatus[device.device_status] = (stats.byStatus[device.device_status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler bei Provider-Statistiken:', error);
      throw error;
    }
  }

  /**
   * Sync-Historie abrufen
   */
  async getSyncHistory(propertyId, providerName = null, limit = 50) {
    try {
      let query = this.supabase
        .from('provider_sync_log')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (providerName) {
        query = query.eq('provider_name', providerName);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Sync-Historie-Abruf:', error);
      throw error;
    }
  }

  /**
   * Abrechnungsdaten abrufen (alle Provider)
   */
  async getBillingData(propertyId, year = null, providerName = null) {
    try {
      let query = this.supabase
        .from('meter_billing_data')
        .select('*')
        .eq('property_id', propertyId)
        .order('billing_period_start', { ascending: false });

      if (year) {
        const startOfYear = `${year}-01-01`;
        const endOfYear = `${year}-12-31`;
        query = query
          .gte('billing_period_start', startOfYear)
          .lte('billing_period_end', endOfYear);
      }

      if (providerName) {
        query = query.eq('provider_name', providerName);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Abrechnungsdaten-Abruf:', error);
      throw error;
    }
  }

  /**
   * Wartungsübersicht für alle Provider
   */
  async getMaintenanceOverview(propertyId) {
    try {
      const { data, error } = await this.supabase
        .from('meter_device_overview')
        .select('*')
        .eq('property_id', propertyId)
        .in('maintenance_status', ['overdue', 'upcoming']);

      if (error) throw error;
      
      // Nach Provider gruppieren
      const byProvider = {};
      data.forEach(device => {
        const provider = device.provider_name || 'manual';
        if (!byProvider[provider]) {
          byProvider[provider] = [];
        }
        byProvider[provider].push(device);
      });

      return {
        devices: data,
        byProvider: byProvider,
        totalOverdue: data.filter(d => d.maintenance_status === 'overdue').length,
        totalUpcoming: data.filter(d => d.maintenance_status === 'upcoming').length
      };
    } catch (error) {
      console.error('[METER-SERVICE] Fehler bei Wartungsübersicht:', error);
      throw error;
    }
  }

  /**
   * Provider entfernen
   */
  async removeProvider(propertyId, providerName) {
    try {
      console.log(`[METER-SERVICE] Entferne ${providerName} für Property ${propertyId}`);

      // Provider-Konfiguration löschen
      await this.supabase
        .from('meter_provider_configs')
        .delete()
        .eq('property_id', propertyId)
        .eq('provider_name', providerName);

      // Geräte auf 'manual' umstellen
      await this.supabase
        .from('meter_points')
        .update({
          provider_name: 'manual',
          provider_device_id: null,
          provider_device_type: null
        })
        .eq('property_id', propertyId)
        .eq('provider_name', providerName);

      // Property-Status aktualisieren wenn das der primäre Provider war
      const { data: property } = await this.supabase
        .from('properties')
        .select('primary_meter_provider')
        .eq('id', propertyId)
        .single();

      if (property?.primary_meter_provider === providerName) {
        // Anderen aktiven Provider finden oder auf 'manual' setzen
        const { data: otherConfigs } = await this.supabase
          .from('meter_provider_configs')
          .select('provider_name')
          .eq('property_id', propertyId)
          .eq('config_status', 'active')
          .limit(1);

        const newPrimaryProvider = otherConfigs?.[0]?.provider_name || 'manual';

        await this.supabase
          .from('properties')
          .update({
            primary_meter_provider: newPrimaryProvider,
            provider_sync_status: newPrimaryProvider === 'manual' ? 'not_configured' : 'configured'
          })
          .eq('id', propertyId);
      }

      return { success: true };
    } catch (error) {
      console.error('[METER-SERVICE] Fehler beim Provider-Entfernen:', error);
      throw error;
    }
  }

  /**
   * Default-Statistiken
   */
  getDefaultStats() {
    return {
      property_id: null,
      property_name: 'Keine Konfiguration',
      total_devices: 0,
      active_devices: 0,
      warning_devices: 0,
      error_devices: 0,
      inactive_devices: 0,
      techem_devices: 0,
      ista_devices: 0,
      brunata_devices: 0,
      manual_devices: 0,
      recent_readings: 0,
      monthly_readings: 0,
      last_reading_date: null,
      overdue_maintenance: 0,
      upcoming_maintenance: 0,
      valid_readings: 0,
      suspicious_readings: 0,
      provider_sync_status: 'not_configured'
    };
  }

  /**
   * Provider-spezifische Icons
   */
  getProviderIcon(providerName) {
    const icons = {
      'techem': '🔥',
      'ista': '💧',
      'brunata': '🌡️',
      'minol': '⚡',
      'kalorimeta': '📊',
      'manual': '✏️'
    };
    
    return icons[providerName] || '📊';
  }

  /**
   * Provider-spezifische Farben
   */
  getProviderColor(providerName) {
    const colors = {
      'techem': 'blue',
      'ista': 'green',
      'brunata': 'purple',
      'minol': 'orange',
      'kalorimeta': 'red',
      'manual': 'gray'
    };
    
    return colors[providerName] || 'gray';
  }

  /**
   * Echtzeit-Updates für alle Provider abonnieren
   */
  subscribeToUpdates(propertyId, callback) {
    console.log(`[METER-SERVICE] Abonniere Updates für Property ${propertyId}`);

    // Sync-Status Updates
    const syncSubscription = this.supabase
      .channel(`meter-sync-${propertyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'properties',
          filter: `id=eq.${propertyId}`
        },
        (payload) => {
          console.log('[METER-SERVICE] Sync-Status Update:', payload);
          callback('sync_status', payload.new);
        }
      )
      .subscribe();

    // Neue Ablesungen (alle Provider)
    const readingsSubscription = this.supabase
      .channel(`meter-readings-${propertyId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'readings'
        },
        (payload) => {
          console.log('[METER-SERVICE] Neue Ablesung:', payload);
          callback('new_reading', payload.new);
        }
      )
      .subscribe();

    // Service-Order Updates
    const ordersSubscription = this.supabase
      .channel(`meter-orders-${propertyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meter_service_orders',
          filter: `property_id=eq.${propertyId}`
        },
        (payload) => {
          console.log('[METER-SERVICE] Service-Order Update:', payload);
          callback('service_order', payload);
        }
      )
      .subscribe();

    return {
      syncSubscription,
      readingsSubscription,
      ordersSubscription,
      unsubscribe: () => {
        syncSubscription.unsubscribe();
        readingsSubscription.unsubscribe();
        ordersSubscription.unsubscribe();
      }
    };
  }
}

export default UniversalMeterService;

